/*
<purpose>Centralized ticket mutation orchestrator — optimistic in-flight tracking, rollback-on-failure, undo toast wiring. No client-side ticket store; all list data owned by per-page pagination hooks.</purpose>
<dependencies>smarttix API (services/api.ts)</dependencies>
<constraints>
  - No poll loop. No ticket array. List data lives in useActivePagination hooks.
  - processingUuids tracks per-ticket pipeline entry; cleared on failure only
    (success path leaves UUID until next navigation or action — prevents ghost reappearance).
  - archivingUuids / deletingUuids drive optimistic row opacity in consumer pages.
</constraints>
*/

import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import { DraftKind, ProcessContext, ReanalysisRequest, ScaffoldSegment, DeclineInstead } from '../types';
import { smarttix } from '../services/api';
import type { ClaimVia, EditDraftSlotResult, EditSuggestionResult, RenderSecondaryDraftResult, TriageActionResult } from '../services/api';
import type { DraftEditableKind, DraftSlotKind } from '../services/draftClaims';
import { announceTicketMutated } from '../services/ticketSync';

// ─── Types ──────────────────────────────────────────────

interface ArchiveUndoState {
  uuid: string;
  previousStatus: string;
}

interface MutationResult {
  success: boolean;
  error?: string;
}

interface TicketManagerContextValue {
  // In-flight tracking (read-only for consumers)
  processingUuids: ReadonlySet<string>;
  deletingUuids: ReadonlySet<string>;
  archivingUuids: ReadonlySet<string>;
  restoringUuids: ReadonlySet<string>;

  // Archive undo
  archiveUndo: ArchiveUndoState | null;

  // Mutations
  processTicket: (uuid: string, processContext?: ProcessContext) => Promise<MutationResult>;
  deleteTicket: (uuid: string) => Promise<MutationResult>;
  archiveTicket: (uuid: string) => Promise<MutationResult>;
  unarchiveTicket: (uuid: string, restoreStatus?: string) => Promise<MutationResult>;
  approveTicket: (uuid: string) => Promise<MutationResult>;
  // „Nicht hilfreich" (2026-10-16) and its undo — announced like every other mutation.
  withdrawApproval: (uuid: string) => Promise<MutationResult>;
  declineTicket: (uuid: string, instead: DeclineInstead | null) => Promise<MutationResult>;
  withdrawDecline: (uuid: string) => Promise<MutationResult>;
  copyApproveTicket: (uuid: string) => Promise<MutationResult>;
  undoCopyApprove: (uuid: string) => Promise<MutationResult>;
  // Draft claim family (Part C, 2026-09-07). These return the API's own result rather
  // than MutationResult: a refusal and a stale row are outcomes the surface explains, and
  // flattening them to {success,error} would throw away the distinction.
  claimDraft: (uuid: string, suggestionVersion: number, kind: DraftKind, via?: ClaimVia) => Promise<TriageActionResult>;
  unclaimDraft: (uuid: string, kind: DraftKind) => Promise<TriageActionResult>;
  renderSecondaryDraft: (uuid: string, suggestionId: number, kind: DraftSlotKind) => Promise<RenderSecondaryDraftResult>;
  // Rewriting ONE composed draft slot. Like the claim family it returns the API's own result: a
  // refusal („Bereits genehmigt.") and a moved-on row are different outcomes with different
  // remedies, and flattening them to {success,error} would lose the one the host reloads on.
  editDraftSlot: (
    uuid: string,
    body: {
      suggestionId: number; kind: DraftEditableKind; text: string;
      // A SLOT FILL rather than a free-text rewrite (Task 6b): the letter's segments as served, so
      // the server rewrites the scaffold beside the text and the next read serves the fill back.
      scaffoldSegments?: ScaffoldSegment[];
    },
  ) => Promise<EditDraftSlotResult>;
  requestReanalysis: (uuid: string, request: ReanalysisRequest) => Promise<MutationResult>;
  unblockReanalysis: (uuid: string) => Promise<MutationResult>;
  // Not MutationResult: the segments PATCH can lose the same race the claim family can, and a
  // stale answer is an outcome the surface RELOADS on rather than an error it reports.
  editSuggestion: (
    uuid: string,
    text: string,
    scaffoldSegments?: ScaffoldSegment[],
    suggestionId?: number,
  ) => Promise<EditSuggestionResult>;
  bulkDeleteTickets: () => Promise<MutationResult & { deletedAt?: string }>;
  undoBulkDelete: (deletedAt: string) => Promise<MutationResult>;

  // Archive undo actions
  undoArchive: () => Promise<void>;
  dismissArchiveUndo: () => void;
}

const TicketManagerContext = createContext<TicketManagerContextValue | null>(null);

export function useTicketManager(): TicketManagerContextValue {
  const ctx = useContext(TicketManagerContext);
  if (!ctx) throw new Error('useTicketManager must be used within TicketManagerProvider');
  return ctx;
}

// ─── Provider ───────────────────────────────────────────

interface ProviderProps {
  children: React.ReactNode;
}

export const TicketManagerProvider: React.FC<ProviderProps> = ({ children }) => {
  const [processingUuids, setProcessingUuids] = useState<Set<string>>(new Set());
  const [deletingUuids, setDeletingUuids] = useState<Set<string>>(new Set());
  const [archivingUuids, setArchivingUuids] = useState<Set<string>>(new Set());
  const [restoringUuids, setRestoringUuids] = useState<Set<string>>(new Set());

  const [archiveUndo, setArchiveUndo] = useState<ArchiveUndoState | null>(null);
  const archiveUndoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Helper: add/remove from Set state ─────────────────

  const addToSet = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, uuid: string) =>
    setter(prev => new Set(prev).add(uuid));
  const removeFromSet = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, uuid: string) =>
    setter(prev => { const n = new Set(prev); n.delete(uuid); return n; });

  // ─── Mutations ─────────────────────────────────────────

  const processTicket = useCallback(async (uuid: string, processContext?: ProcessContext): Promise<MutationResult> => {
    addToSet(setProcessingUuids, uuid);

    const result = await smarttix.processTicket(uuid, processContext);

    // Why: only remove on failure. On success, keep UUID in processingUuids —
    // consumer pages clear it via navigation or next action. Prevents ghost
    // reappearance during cold FA start when /process returns before pipeline begins.
    if (!result.success) {
      removeFromSet(setProcessingUuids, uuid);
    }
    if (result.success) announceTicketMutated(uuid);
    return result;
  }, []);

  const deleteTicket = useCallback(async (uuid: string): Promise<MutationResult> => {
    addToSet(setDeletingUuids, uuid);

    const result = await smarttix.deleteTicket(uuid);

    removeFromSet(setDeletingUuids, uuid);
    if (result.success) announceTicketMutated(uuid);
    return result;
  }, []);

  const archiveTicket = useCallback(async (uuid: string): Promise<MutationResult> => {
    addToSet(setArchivingUuids, uuid);

    const result = await smarttix.archiveTicket(uuid);

    removeFromSet(setArchivingUuids, uuid);
    if (result.success) {
      const previousStatus = result.previousStatus || 'approved';
      // Set up undo toast
      if (archiveUndoTimerRef.current) clearTimeout(archiveUndoTimerRef.current);
      setArchiveUndo({ uuid, previousStatus });
      archiveUndoTimerRef.current = setTimeout(() => setArchiveUndo(null), 5000);
      announceTicketMutated(uuid);
    }
    return { success: result.success, error: result.error };
  }, []);

  const unarchiveTicket = useCallback(async (uuid: string, restoreStatus?: string): Promise<MutationResult> => {
    addToSet(setRestoringUuids, uuid);

    const result = await smarttix.unarchiveTicket(uuid, restoreStatus);

    removeFromSet(setRestoringUuids, uuid);
    if (result.success) announceTicketMutated(uuid);
    return result;
  }, []);

  const approveTicket = useCallback(async (uuid: string): Promise<MutationResult> => {
    const result = await smarttix.approveTicket(uuid);
    if (result.success) announceTicketMutated(uuid);
    return result;
  }, []);

  const withdrawApproval = useCallback(async (uuid: string): Promise<MutationResult> => {
    const result = await smarttix.withdrawApproval(uuid);
    if (result.success) announceTicketMutated(uuid);
    return result;
  }, []);

  const declineTicket = useCallback(async (uuid: string, instead: DeclineInstead | null): Promise<MutationResult> => {
    const result = await smarttix.declineTicket(uuid, instead);
    if (result.success) announceTicketMutated(uuid);
    return result;
  }, []);

  const withdrawDecline = useCallback(async (uuid: string): Promise<MutationResult> => {
    const result = await smarttix.withdrawDecline(uuid);
    if (result.success) announceTicketMutated(uuid);
    return result;
  }, []);

  // Copy = indirect approval. Unlike approveTicket, status is left untouched —
  // the ticket stays in Offen; only copyApprovedAt is set. undoCopyApprove
  // mirrors this as the write-then-revert undo (clears copyApprovedAt).
  const copyApproveTicket = useCallback(async (uuid: string): Promise<MutationResult> => {
    const result = await smarttix.copyApproveTicket(uuid);
    if (result.success) announceTicketMutated(uuid);
    return result;
  }, []);

  const undoCopyApprove = useCallback(async (uuid: string): Promise<MutationResult> => {
    const result = await smarttix.undoCopyApprove(uuid);
    if (result.success) announceTicketMutated(uuid);
    return result;
  }, []);

  // Claiming, releasing and composing all mutate the row, so they ride the same chokepoint as
  // every other mutation — announce on success only, so a refused claim never refreshes.
  const claimDraft = useCallback(async (
    uuid: string, suggestionVersion: number, kind: DraftKind, via?: ClaimVia,
  ): Promise<TriageActionResult> => {
    const result = await smarttix.claimDraft(uuid, suggestionVersion, kind, via);
    if (result.success) announceTicketMutated(uuid);
    return result;
  }, []);

  const unclaimDraft = useCallback(async (uuid: string, kind: DraftKind): Promise<TriageActionResult> => {
    const result = await smarttix.unclaimDraft(uuid, kind);
    if (result.success) announceTicketMutated(uuid);
    return result;
  }, []);

  const renderSecondaryDraft = useCallback(async (
    uuid: string, suggestionId: number, kind: DraftSlotKind,
  ): Promise<RenderSecondaryDraftResult> => {
    const result = await smarttix.renderSecondaryDraft(uuid, suggestionId, kind);
    if (result.success) announceTicketMutated(uuid);
    return result;
  }, []);

  // Through the chokepoint like every other write, so a rewrite reaches every open window (the
  // pop-out above all) instead of only the pane the clerk typed in.
  const editDraftSlot = useCallback(async (
    uuid: string,
    body: {
      suggestionId: number; kind: DraftEditableKind; text: string;
      scaffoldSegments?: ScaffoldSegment[];
    },
  ): Promise<EditDraftSlotResult> => {
    const result = await smarttix.editDraftSlot(uuid, body);
    if (result.success) announceTicketMutated(uuid);
    return result;
  }, []);

  const requestReanalysis = useCallback(async (uuid: string, request: ReanalysisRequest): Promise<MutationResult> => {
    const result = await smarttix.requestReanalysis(uuid, request);
    if (result.success) announceTicketMutated(uuid);
    return result;
  }, []);

  const unblockReanalysis = useCallback(async (uuid: string): Promise<MutationResult> => {
    const result = await smarttix.unblockReanalysis(uuid);
    if (result.success) announceTicketMutated(uuid);
    return result;
  }, []);

  // Suggestion edit lived as a direct smarttix call in TicketDetail; folded here so the
  // mutation chokepoint stays complete and every window hears about the edit.
  const editSuggestion = useCallback(async (
    uuid: string,
    text: string,
    scaffoldSegments?: ScaffoldSegment[],
    suggestionId?: number,
  ): Promise<EditSuggestionResult> => {
    // Conditional call shape (not just an undefined 3rd/4th arg) keeps the no-scaffold call site
    // byte-identical to pre-Task-7 — same arg count reaches the API layer, same PATCH body.
    // suggestionId only ever rides ALONGSIDE scaffoldSegments (fix-review round 1) — it targets
    // the exact ai_suggestions row the fill was confirmed against, never "latest for this uuid".
    const result = scaffoldSegments
      ? await smarttix.editSuggestion(uuid, text, scaffoldSegments, suggestionId)
      : await smarttix.editSuggestion(uuid, text);
    if (result.success) announceTicketMutated(uuid);
    return result;
  }, []);

  const bulkDeleteTickets = useCallback(async (): Promise<MutationResult & { deletedAt?: string }> => {
    const result = await smarttix.bulkDeleteTickets();

    if ('success' in result && result.success === false) {
      return { success: false, error: result.error };
    }
    announceTicketMutated('*');
    return { success: true, deletedAt: (result as any).deletedAt };
  }, []);

  const undoBulkDelete = useCallback(async (deletedAt: string): Promise<MutationResult> => {
    const result = await smarttix.undoBulkDelete(deletedAt);
    if (result.success) announceTicketMutated('*');
    return result;
  }, []);

  // ─── Archive undo ──────────────────────────────────────

  const undoArchive = useCallback(async () => {
    if (!archiveUndo) return;
    if (archiveUndoTimerRef.current) clearTimeout(archiveUndoTimerRef.current);
    const { uuid, previousStatus } = archiveUndo;
    setArchiveUndo(null);
    await unarchiveTicket(uuid, previousStatus);
  }, [archiveUndo, unarchiveTicket]);

  const dismissArchiveUndo = useCallback(() => {
    if (archiveUndoTimerRef.current) clearTimeout(archiveUndoTimerRef.current);
    setArchiveUndo(null);
  }, []);

  // ─── Context value ─────────────────────────────────────

  const value = useMemo<TicketManagerContextValue>(() => ({
    processingUuids, deletingUuids, archivingUuids, restoringUuids,
    archiveUndo,
    processTicket, deleteTicket, archiveTicket, unarchiveTicket,
    approveTicket, withdrawApproval, declineTicket, withdrawDecline, copyApproveTicket, undoCopyApprove, requestReanalysis, unblockReanalysis,
    claimDraft, unclaimDraft, renderSecondaryDraft, editDraftSlot,
    editSuggestion,
    bulkDeleteTickets, undoBulkDelete,
    undoArchive, dismissArchiveUndo,
  }), [
    processingUuids, deletingUuids, archivingUuids, restoringUuids,
    archiveUndo,
    processTicket, deleteTicket, archiveTicket, unarchiveTicket,
    approveTicket, withdrawApproval, declineTicket, withdrawDecline, copyApproveTicket, undoCopyApprove, requestReanalysis, unblockReanalysis,
    claimDraft, unclaimDraft, renderSecondaryDraft, editDraftSlot,
    editSuggestion,
    bulkDeleteTickets, undoBulkDelete,
    undoArchive, dismissArchiveUndo,
  ]);

  return (
    <TicketManagerContext.Provider value={value}>
      {children}
    </TicketManagerContext.Provider>
  );
};
